import { FunctionComponent, useEffect, useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { TableRow, TableCell, Typography, Box } from '@material-ui/core';
import clsx from 'clsx';
import ToolbarButton from 'components/UI/Toolbar/ToolbarButton';
import ToolbarItem from 'components/UI/Toolbar/ToolbarItem';
import { faBan, faCheckCircle, faPen } from '@fortawesome/pro-light-svg-icons';
import ProductVendor from 'model/ProductVendor';
import ChangeStatusItemWithConfirmation from '../Vendor/ChangeStatusItem';
import { useDispatch } from 'react-redux';
import { saveThunk } from 'actions/productVendors/ProductVendorsActions';
import { formatPhoneNumber } from '@spike/phone-utils';
import WarningDialog from '../UI/WarningDialog';
import { useMarketplace } from 'hooks';

interface Props {
    vendor: ProductVendor;
    onEdit: (vendor: ProductVendor) => void;
    mobile?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        row: {
            'backgroundColor': '#FFFFFF',
            'height': 'auto',
            '&:hover': {
                backgroundColor: '#FAFAFA !important',
                cursor: 'pointer'
            }
        },
        cell: {
            paddingLeft: '0px',
            paddingRight: '24px',
            verticalAlign: 'top !important',
            borderBottom: '1px solid #DDDDDD',
            [theme.breakpoints.down('md')]: {
                fontSize: '15px',
                paddingTop: '21px',
                paddingBottom: '21px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '18px',
                paddingTop: '26px',
                paddingBottom: '26px'
            }
        },
        options: {
            display: 'flex',
            justifyContent: 'flex-end',
            flexShrink: 0
        },
        firstCell: {
            [theme.breakpoints.down(768)]: {
                paddingLeft: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingLeft: 27
            },
            [theme.breakpoints.only('md')]: {
                paddingLeft: 43
            },
            [theme.breakpoints.only('lg')]: {
                paddingLeft: 57
            },
            [theme.breakpoints.only('xl')]: {
                paddingLeft: 87
            }
        },
        lastCell: {
            textAlign: 'right',
            [theme.breakpoints.down(768)]: {
                paddingRight: 16
            },
            [theme.breakpoints.between(768, 'sm')]: {
                paddingRight: 40
            },
            [theme.breakpoints.only('md')]: {
                paddingRight: 65
            },
            [theme.breakpoints.only('lg')]: {
                paddingRight: 87
            },
            [theme.breakpoints.only('xl')]: {
                paddingRight: 132
            }
        },
        text: {
            fontWeight: 400,
            [theme.breakpoints.down('md')]: {
                fontSize: '13px',
                lineHeight: '22px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px',
                lineHeight: '27px'
            }
        },
        textDialog: {
            textAlign: 'center',
            [theme.breakpoints.up('lg')]: {
                fontSize: '15px'
            }
        },
        vendorsNameCells: {
            width: '20%'
        },
        vendorsCells: {
            width: '12%'
        },
        vendorsEmailCells: {
            width: '17%'
        },
        vendorsStatusCells: {
            width: '7%'
        },
        vendorsSmallCells: {
            width: '10%'
        },
        products: {
            [theme.breakpoints.down('md')]: {
                marginLeft: '22%'
            },
            [theme.breakpoints.up('lg')]: {
                marginLeft: '19%'
            }
        },
        typoDelete: {
            fontWeight: 400,
            [theme.breakpoints.down('md')]: {
                fontSize: '13px'
            },
            [theme.breakpoints.up('lg')]: {
                fontSize: '16px'
            }
        },
        deactivated: {
            color: '#00000080 !important'
        },
        mobileCard: {
            'display': 'flex',
            'flexDirection': 'column',
            'gap': '8px',
            'width': '100%',
            'padding': '16px',
            'background': '#FFFFFF',
            'borderBottom': '1px solid #DDDDDD',
            'cursor': 'pointer',
            '&:hover': {
                backgroundColor: '#FAFAFA'
            }
        },
        mobileName: {
            fontWeight: 600,
            fontSize: '14px',
            lineHeight: '20px',
            color: '#222222',
            wordBreak: 'break-word'
        },
        mobileTopMeta: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            minWidth: 0
        },
        mobileMetaText: {
            fontSize: '13px',
            lineHeight: '18px',
            color: '#7A7A7A',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1
        },
        mobileStatus: {
            flexShrink: 0,
            fontSize: '13px',
            lineHeight: '18px',
            fontWeight: 500,
            color: '#222222'
        },
        mobileBottom: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            minWidth: 0
        },
        mobileMetrics: {
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '16px',
            minWidth: 0,
            flex: 1
        },
        mobileMetricText: {
            fontSize: '13px',
            lineHeight: '18px',
            color: '#222222',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '100%'
        }
    })
);

export const VendorRow: FunctionComponent<Props> = props => {
    const classes = useStyles();
    const marketplace = useMarketplace();

    const [vendor, setVendor] = useState<ProductVendor>(props.vendor);
    const [showWarningAssignedProducts, setShowWarningAssignedProducts] = useState(false);

    const dispatch = useDispatch();

    useEffect(() => {
        setVendor(props.vendor);
    }, [props.vendor]);

    const handleChangeStatus = (action: string) => {
        const vendorAux = { ...vendor };
        if (action === 'activate') {
            vendorAux.active = true;
        } else {
            vendorAux.active = false;
        }
        setVendor(vendorAux);
        dispatch(saveThunk(vendorAux));
    };

    const phone = vendor.phone ? formatPhoneNumber(vendor.phone, marketplace.basics.address.country!.id) : '-';

    const toolbar = (
        <Box
            className={classes.options}
            onClick={event => event.stopPropagation()}
        >
            <ToolbarButton>
                <ToolbarItem
                    icon={faPen}
                    onClick={() => props.onEdit(vendor)}
                    text={'Edit/view'}
                />
                {vendor.active ? (
                    vendor.products.length === 0 ? (
                        <Box>
                            <ChangeStatusItemWithConfirmation
                                icon={faBan}
                                title="Deactivate"
                                text="Deactivate"
                                onConfirmedClick={() => handleChangeStatus('deactivate')}
                                confirmQuestion={
                                    <Typography className={classes.typoDelete}>
                                        An inactive vendor won’t be able to be attach to any product.
                                    </Typography>
                                }
                                confirmButtonLabel="Deactivate"
                                cancelButtonLabel="Cancel"
                            />
                        </Box>
                    ) : (
                        <ToolbarItem
                            icon={faBan}
                            onClick={() => setShowWarningAssignedProducts(true)}
                            text={'Deactivate'}
                        />
                    )
                ) : (
                    <Box>
                        <ChangeStatusItemWithConfirmation
                            icon={faCheckCircle}
                            title="Activate"
                            text={'Activate'}
                            onConfirmedClick={() => handleChangeStatus('activate')}
                            confirmQuestion={
                                <Typography className={classes.typoDelete}>
                                    Are you sure you want to activate vendor?
                                </Typography>
                            }
                            confirmButtonLabel="Activate"
                            cancelButtonLabel="Cancel"
                        />
                    </Box>
                )}
            </ToolbarButton>
        </Box>
    );

    const warningDialog = (
        <WarningDialog
            open={showWarningAssignedProducts}
            title={`(${vendor.products.length}) products currently assigned to this vendor."`}
            description="In order to deactivate, you must first remove products assigned to this vendor."
            onOk={() => setShowWarningAssignedProducts(false)}
        />
    );

    if (props.mobile) {
        return (
            <>
                <div
                    className={classes.mobileCard}
                    onClick={() => props.onEdit(vendor)}
                >
                    <div
                        className={clsx(classes.mobileName, {
                            [classes.deactivated]: !vendor.active
                        })}
                    >
                        {vendor.name}
                    </div>
                    <div className={classes.mobileTopMeta}>
                        <span
                            className={clsx(classes.mobileMetaText, {
                                [classes.deactivated]: !vendor.active
                            })}
                        >
                            {vendor.contact || '-'}
                        </span>
                        <span
                            className={clsx(classes.mobileStatus, {
                                [classes.deactivated]: !vendor.active
                            })}
                        >
                            {vendor.active ? 'Active' : 'Inactive'}
                        </span>
                    </div>
                    <div className={classes.mobileBottom}>
                        <div className={classes.mobileMetrics}>
                            <span
                                className={clsx(classes.mobileMetricText, {
                                    [classes.deactivated]: !vendor.active
                                })}
                            >
                                Products: {vendor.products.length}
                            </span>
                            <span
                                className={clsx(classes.mobileMetricText, {
                                    [classes.deactivated]: !vendor.active
                                })}
                            >
                                {phone}
                            </span>
                        </div>
                        {toolbar}
                    </div>
                </div>
                {warningDialog}
            </>
        );
    }

    return (
        <>
            <TableRow className={clsx(classes.row)}>
                <TableCell
                    className={clsx(classes.cell, classes.firstCell, classes.vendorsNameCells)}
                    onClick={() => props.onEdit(vendor)}
                >
                    <Typography
                        className={clsx(classes.text, {
                            [classes.deactivated]: !vendor.active
                        })}
                    >
                        {vendor.name}
                    </Typography>
                </TableCell>
                <TableCell
                    className={clsx(classes.cell, classes.vendorsCells)}
                    onClick={() => props.onEdit(vendor)}
                >
                    <Typography
                        className={clsx(classes.text, {
                            [classes.deactivated]: !vendor.active
                        })}
                    >
                        {vendor.contact ? vendor.contact : '-'}
                    </Typography>
                </TableCell>
                <TableCell
                    className={clsx(classes.cell, classes.vendorsCells)}
                    onClick={() => props.onEdit(vendor)}
                >
                    <Typography
                        className={clsx(classes.text, {
                            [classes.deactivated]: !vendor.active
                        })}
                    >
                        {phone}
                    </Typography>
                </TableCell>
                <TableCell
                    className={clsx(classes.cell, classes.vendorsEmailCells)}
                    onClick={() => props.onEdit(vendor)}
                >
                    <Typography
                        className={clsx(classes.text, {
                            [classes.deactivated]: !vendor.active
                        })}
                    >
                        {vendor.email ? vendor.email : '-'}
                    </Typography>
                </TableCell>
                <TableCell
                    className={clsx(classes.cell, classes.vendorsEmailCells)}
                    onClick={() => props.onEdit(vendor)}
                >
                    <Typography
                        className={clsx(classes.text, {
                            [classes.deactivated]: !vendor.active
                        })}
                    >
                        {vendor.website ? vendor.website : '-'}
                    </Typography>
                </TableCell>
                <TableCell
                    className={clsx(classes.cell, classes.vendorsStatusCells)}
                    onClick={() => props.onEdit(vendor)}
                >
                    <Typography
                        className={clsx(classes.text, {
                            [classes.deactivated]: !vendor.active
                        })}
                    >
                        {vendor?.address?.state ? vendor.address?.state : '-'}
                    </Typography>
                </TableCell>
                <TableCell
                    className={clsx(classes.cell, classes.vendorsStatusCells, classes.products)}
                    onClick={() => props.onEdit(vendor)}
                >
                    <Typography
                        className={clsx(classes.text, classes.products, {
                            [classes.deactivated]: !vendor.active
                        })}
                    >
                        {vendor.products.length}
                    </Typography>
                </TableCell>
                <TableCell
                    className={clsx(classes.cell, classes.vendorsStatusCells)}
                    onClick={() => props.onEdit(vendor)}
                >
                    <Typography
                        className={clsx(classes.text, {
                            [classes.deactivated]: !vendor.active
                        })}
                    >
                        {vendor.active ? 'Active' : 'Inactive'}
                    </Typography>
                </TableCell>
                <TableCell className={clsx(classes.cell, classes.vendorsSmallCells, classes.lastCell)}>
                    {toolbar}
                </TableCell>
            </TableRow>
            {warningDialog}
        </>
    );
};

export default VendorRow;
